/**
 * @file onboarding-blackout.component.ts
 * @description Componente visual del sistema de Onboarding.
 *
 * Pipeline de _calculatePositions (async):
 *  a) query sincrono (visible)     -> elemento ya en DOM (90% de los casos)
 *  b) _waitForElement()            -> MutationObserver, espera hasta 15s (cards HTTP, cold boot, API lenta)
 *  b2) scrollIntoView condicional  -> trae el objetivo a la vista si quedó fuera del viewport
 *                                     (scroll heredado del paso anterior u objetivo que nace fuera — FINA-3297)
 *  c) await delay(500ms)           -> deja que terminen reflows de layout global
 *                                     (ej. sidebar colapsandose/abriendose, el scrollIntoView de b2)
 *  d) getBoundingClientRect()      -> medicion con geometria estabilizada
 *  e) Fallback: omision automatica -> si a/b fallan (advertencia + analitica)
 *
 * Change Detection:
 *  El pipeline corre fuera de la zona de Angular (NgZone.runOutsideAngular).
 *  Al terminar, re-entra con NgZone.run() + detectChanges() para forzar el
 *  re-render inmediatamente, sin depender del ciclo de CD de OnPush.
 */

import { ChangeDetectionStrategy, ChangeDetectorRef, Component, HostListener, NgZone, OnDestroy, OnInit } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { TourStep, TourStepPlacement } from '../../interfaces/tour.interfaces';
import { OnboardingAnalyticsService } from '../../services/onboarding-analytics.service';
import { TourOrchestratorService } from '../../services/tour-orchestrator.service';

// ─── Constantes ───────────────────────────────────────────────────────────────

const SPOTLIGHT_PADDING = 6;
const POPOVER_OFFSET = 16;
const POPOVER_MAX_WIDTH = 340;

/**
 * Tiempo maximo (ms) que _waitForElement esperara a que un elemento
 * aparezca en el DOM via MutationObserver.
 * Con la API lenta (QA midió +6 s por petición) el objetivo del paso se pintaba DESPUÉS del
 * tope anterior (4,5 s) y el paso se omitía en silencio aunque su elemento aparecía un instante más tarde
 * (el badge saltaba de 1 a 3). Se amplía a 15 s (propuesta de QA: 15–20 s). La espera ya no es una pantalla
 * congelada: el overlay muestra un estado de carga (`.tour-loading`, con fade-in retrasado para no parpadear
 * en los cambios de paso normales) y, si aun así el tope se agota, la omisión se registra en analítica
 * (`stepAutoSkipped`) además del aviso de consola.
 */
const WAIT_FOR_ELEMENT_TIMEOUT_MS = 15000;

/**
 * Delay fijo (ms) para absorber reflows de layout globales antes de medir.
 *
 * Justificacion arquitectonica:
 * El paso 1 del tour de Pedidos tiene una race condition entre el sidebar
 * que se abre (NavigationService.expandSideNav()) y getBoundingClientRect().
 * Aunque el elemento #nav-pedidos ya existe en el DOM, su posicion final
 * depende de que el contenedor padre (sidebar) termine su transicion CSS.
 *
 * Un delay fijo de 500ms es el estandar para arquitecturas "Cero Cables"
 * cuando hay reflows de layout globales (colapsar/expandir un menu),
 * ya que el componente no puede ni debe conocer al NavigationService.
 *
 * El delay solo se aplica una vez por paso (no por cada frame).
 * Para los pasos 2-5 (sin animacion de layout), el costo es 500ms aceptables.
 */
const LAYOUT_SETTLE_MS = 500;

/** Ancho maximo del viewport (px) considerado como movil. */
const MOBILE_BREAKPOINT = 768;

/** Margen horizontal minimo (px) entre el popover y los bordes del viewport en movil. */
const SIDE_MARGIN_PX = 16;

/**
 * Altura estimada (px) del popover para la PRIMERA pasada de posicionamiento en escritorio, antes de que
 * exista en el DOM (su altura real depende del contenido del paso). Tras el primer render,
 * `_refinePopoverGeometry` mide la altura real, la cachea y re-aplica la geometría de forma SÍNCRONA
 * (antes del paint), así que este valor solo influye en un cálculo intermedio nunca visible.
 */
const POPOVER_FALLBACK_HEIGHT_PX = 240;

/**
 * Espacio minimo (px) por debajo del elemento para preferir placement 'bottom' en movil.
 * Si el espacio es menor, se usa 'top'. Cubre la altura aproximada del popover + caret.
 */
const MIN_SPACE_FOR_POPOVER_PX = 180;

// ─── Interfaces internas ──────────────────────────────────────────────────────

interface SpotlightRect {
    top: number;
    left: number;
    width: number;
    height: number;
    borderRadius: number;
}

interface PopoverPosition {
    top: string;
    left: string;
    transform: string;
}

// ─── Componente ───────────────────────────────────────────────────────────────

@Component({
    selector: 'fina-onboarding-blackout',
    templateUrl: './onboarding-blackout.component.html',
    styleUrls: ['./onboarding-blackout.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OnboardingBlackoutComponent implements OnInit, OnDestroy {
    // ─── Estado publico ───────────────────────────────────────────────────────

    isVisible = false;
    currentStep: TourStep | null = null;
    currentStepDisplay = 1;
    totalSteps = 0;
    spotlightRect: SpotlightRect | null = null;
    popoverPosition: PopoverPosition = { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' };
    currentPlacement: TourStepPlacement = 'bottom';
    elementNotFound = false;
    progressPercent = 0;

    /**
     * Controla la transición CSS del spotlight. `true` al CAMBIAR de paso (el resaltado desliza suave hacia el
     * nuevo elemento); `false` durante el SCROLL (el resaltado debe seguir al elemento al instante, sin lag). Sin
     * esto, la transición de 0.35s hace que el "agujero" persiga con retraso y luego cuadre al soltar el scroll.
     */
    spotlightAnimated = true;

    /**
     * True mientras el pipeline async busca el elemento y espera el layout settle.
     * Durante este tiempo el overlay oscurece la pantalla pero el spotlight
     * y el popover estan ocultos (via *ngIf en el template) para evitar
     * el flash visual descuadrado de la race condition.
     */
    isCalculating = false;

    /**
     * Distancia en px desde el borde izquierdo del popover hasta el centro
     * horizontal del elemento objetivo. Se asigna como variable CSS --caret-x
     * en el template para que la flecha apunte al elemento incluso cuando el
     * popover fue desplazado por el boundary clamping en movil.
     * null en escritorio (el caret se centra con CSS estatico al 50%).
     */
    caretOffsetPx: number | null = null;

    // ─── Internos ─────────────────────────────────────────────────────────────

    private readonly _destroy$ = new Subject<void>();
    private _destroyed = false;
    private _resizeScheduled = false;

    /**
     * Token de secuencia: se incrementa en CADA emisión de `tourState$` (y en cada resize/cierre). El pipeline
     * async captura su valor y verifica que siga siendo el vigente antes de escribir estado. Cancela pipelines
     * solapados (ej. pulsar "Siguiente" antes del `LAYOUT_SETTLE_MS`) para que el spotlight del paso anterior no
     * repinte sobre el nuevo. El guard `_destroyed` no basta: este componente vive en AppComponent y no se destruye.
     */
    private _calcSeq = 0;

    /** Handler de scroll (registrado FUERA de la zona de Angular) para reposicionar el tour en vivo. */
    private _scrollHandler: (() => void) | null = null;

    // FINA-3294 (CP-01/CB-02): reenvío de rueda/gesto táctil del overlay a los contenedores internos.
    private _wheelHandler: ((e: WheelEvent) => void) | null = null;
    private _touchStartHandler: ((e: TouchEvent) => void) | null = null;
    private _touchMoveHandler: ((e: TouchEvent) => void) | null = null;
    private _touchEndHandler: (() => void) | null = null;

    /**
     * Estado del gesto táctil en curso sobre el overlay. `container` se resuelve en el PRIMER movimiento (la
     * dirección define si el contenedor puede scrollear) y queda fijado para todo el gesto: `undefined` =
     * aún sin resolver, `null` = no hay contenedor interno (dejar el paneo nativo de la página).
     */
    private _touchForward: { lastY: number; container?: HTMLElement | null } | null = null;

    /**
     * Altura REAL (px) del popover medida tras el render del paso actual (`_refinePopoverGeometry`). La usa
     * `_computePopoverPosition` para el flip vertical (bottom↔top) y el clamp al viewport en escritorio.
     * Arranca con la estimación de fallback; el reposicionamiento por scroll reutiliza el valor cacheado.
     */
    private _popoverHeightPx = POPOVER_FALLBACK_HEIGHT_PX;
    /** Guard de requestAnimationFrame: throttlea el reposicionamiento por scroll a un update por frame. */
    private _scrollRaf: number | null = null;

    constructor(
        public readonly orchestrator: TourOrchestratorService,
        private readonly _cdRef: ChangeDetectorRef,
        private readonly _ngZone: NgZone,
        private readonly analytics: OnboardingAnalyticsService,
    ) {}

    // ─── Ciclo de vida ────────────────────────────────────────────────────────

    ngOnInit(): void {
        this.orchestrator.tourState$.pipe(takeUntil(this._destroy$)).subscribe(state => {
            this.isVisible = state.isActive;

            if (state.isActive && state.currentTour) {
                const step = state.currentTour.steps[state.currentStepIndex];
                this.currentStep = step;
                this.currentStepDisplay = state.currentStepIndex + 1;
                this.totalSteps = state.currentTour.steps.length;
                this.progressPercent = Math.round(((state.currentStepIndex + 1) / this.totalSteps) * 100);
                this.currentPlacement = step.placement;
                this.spotlightAnimated = true; // cambio de paso → el spotlight desliza suave al nuevo elemento
                this._setBodyLock(true);

                // Activar el estado de calculo ANTES de lanzar el pipeline.
                // El overlay oscurece la pantalla inmediatamente pero el
                // spotlight y el popover quedan ocultos hasta que el pipeline
                // termine con geometria estabilizada.
                this.isCalculating = true;
                this._cdRef.markForCheck();

                // Nueva secuencia: invalida cualquier pipeline en vuelo de un paso anterior.
                const seq = ++this._calcSeq;
                this._ngZone.runOutsideAngular(() => {
                    requestAnimationFrame(() => {
                        this._calculatePositions(step, seq)
                            .then(() => {
                                // Re-entrar a la zona de Angular y forzar CD.
                                // detectChanges() es obligatorio aqui: markForCheck()
                                // solo marca el componente como "sucio" pero no
                                // dispara el ciclo de CD inmediatamente en OnPush.
                                // detectChanges() lo fuerza sincrono.
                                this._ngZone.run(() => {
                                    if (this._destroyed || seq !== this._calcSeq) return;
                                    this.isCalculating = false;
                                    this._cdRef.detectChanges();
                                    // Con el popover ya en el DOM, medir su altura real y corregir el
                                    // clamp/flip si difiere de la estimada (síncrono: antes del paint).
                                    this._refinePopoverGeometry();
                                });
                            })
                            .catch(err => {
                                // Si beforeShowAction() (async, DOM) rechaza, sin este catch la promesa
                                // queda sin manejar e `isCalculating` se queda en true para siempre →
                                // overlay congelado. Limpiamos el estado y caemos al popover centrado.
                                console.error('[OnboardingBlackout] Error en pipeline de posicionamiento:', err);
                                this._ngZone.run(() => {
                                    if (this._destroyed || seq !== this._calcSeq) return;
                                    this.isCalculating = false;
                                    this.elementNotFound = true;
                                    this.popoverPosition = { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' };
                                    this._cdRef.detectChanges();
                                });
                            });
                    });
                });
            } else {
                // Fin del tour / paso inactivo: invalida cualquier pipeline en vuelo (no debe repintar el
                // overlay ya cerrado) y limpia el estado.
                this._calcSeq++;
                this.currentStep = null;
                this.spotlightRect = null;
                this.elementNotFound = false;
                this.isCalculating = false;
                this.caretOffsetPx = null;
                this._setBodyLock(false);
                this._cdRef.markForCheck();
            }
        });

        // Reposicionamiento en vivo al hacer scroll: el tour permite desplazar la página (ver `_setBodyLock`),
        // así que spotlight y popover deben seguir al elemento objetivo. Se registra FUERA de la zona de Angular
        // y se throttlea con rAF para no disparar change-detection por cada evento de scroll. `capture: true`
        // atrapa también el scroll de contenedores internos (el contenido de la plataforma scrollea en su propio
        // wrapper, no en `window`).
        this._ngZone.runOutsideAngular(() => {
            this._scrollHandler = () => {
                if (this._scrollRaf !== null) return;
                this._scrollRaf = requestAnimationFrame(() => {
                    this._scrollRaf = null;
                    this._repositionOnScroll();
                });
            };
            window.addEventListener('scroll', this._scrollHandler, true);

            // FINA-3294 (CP-01/CB-02): con el tour activo, el overlay (`pointer-events: all`) captura la rueda
            // y el gesto táctil; el encadenamiento nativo solo alcanza el scroll del DOCUMENTO, nunca los
            // paneles INTERNOS bajo el puntero (lista de productos y detalle de la venta en el POS: 0px con
            // tour, 136/291px sin tour — QA). Estos handlers reenvían el desplazamiento al contenedor
            // scrolleable bajo el punto; si no hay (o llegó a su tope en esa dirección), no tocan nada y el
            // navegador desplaza la página como hasta ahora. `passive: false` es necesario para preventDefault
            // (evitar el DOBLE scroll contenedor+página).
            this._wheelHandler = (e: WheelEvent) => this._forwardWheelToInnerScrollable(e);
            document.addEventListener('wheel', this._wheelHandler, { passive: false });
            this._touchStartHandler = (e: TouchEvent) => this._beginTouchForward(e);
            document.addEventListener('touchstart', this._touchStartHandler, { passive: true });
            this._touchMoveHandler = (e: TouchEvent) => this._forwardTouchToInnerScrollable(e);
            document.addEventListener('touchmove', this._touchMoveHandler, { passive: false });
            this._touchEndHandler = () => {
                this._touchForward = null;
            };
            document.addEventListener('touchend', this._touchEndHandler, { passive: true });
        });
    }

    ngOnDestroy(): void {
        this._destroyed = true;
        this._setBodyLock(false);
        if (this._scrollHandler) {
            window.removeEventListener('scroll', this._scrollHandler, true);
            this._scrollHandler = null;
        }
        if (this._wheelHandler) {
            document.removeEventListener('wheel', this._wheelHandler);
            this._wheelHandler = null;
        }
        if (this._touchStartHandler) {
            document.removeEventListener('touchstart', this._touchStartHandler);
            this._touchStartHandler = null;
        }
        if (this._touchMoveHandler) {
            document.removeEventListener('touchmove', this._touchMoveHandler);
            this._touchMoveHandler = null;
        }
        if (this._touchEndHandler) {
            document.removeEventListener('touchend', this._touchEndHandler);
            this._touchEndHandler = null;
        }
        if (this._scrollRaf !== null) {
            cancelAnimationFrame(this._scrollRaf);
            this._scrollRaf = null;
        }
        this._destroy$.next();
        this._destroy$.complete();
    }

    // ─── Helpers privados ─────────────────────────────────────────────────────

    private _setBodyLock(lock: boolean): void {
        document.body.classList.toggle('tour-lock', lock);
    }

    // ─── Reenvío de scroll a contenedores internos (FINA-3294: CP-01/CB-02) ───

    /** True si el evento cayó sobre el overlay oscuro del tour (no sobre el popover ni fuera del Blackout).
     *  Dentro del popover, y a través del agujero del spotlight (`pointer-events: none`), todo sigue nativo. */
    private _isEventOnOverlay(target: EventTarget | null): boolean {
        if (!this.isVisible) return false;
        const el = target as Element | null;
        if (!el || typeof el.closest !== 'function') return false;
        return !!el.closest('.tour-overlay') && !el.closest('.tour-popover');
    }

    /**
     * Contenedor con scroll vertical PROPIO bajo el punto (x, y), ignorando el Blackout, que además pueda
     * desplazarse en la dirección pedida. Se parte del primer elemento REAL de la app bajo el overlay
     * (`elementsFromPoint`) y se sube por sus ancestros. Sin contenedor (o al tope en esa dirección) → null:
     * el navegador desplaza la página de forma nativa, como hasta ahora (el encadenamiento queda natural:
     * al agotarse el panel interno, sigue la página).
     */
    private _findInnerScrollableAt(x: number, y: number, deltaY: number): HTMLElement | null {
        const stack = document.elementsFromPoint(x, y);
        const appEl = stack.find(el => !el.closest('.tour-overlay') && !el.closest('.tour-popover'));
        if (!appEl) return null;
        for (let node: HTMLElement | null = appEl as HTMLElement; node && node !== document.body && node !== document.documentElement; node = node.parentElement) {
            if (node.scrollHeight > node.clientHeight + 1) {
                const overflowY = getComputedStyle(node).overflowY;
                if (overflowY === 'auto' || overflowY === 'scroll') {
                    const canScrollDown = node.scrollTop + node.clientHeight < node.scrollHeight - 1;
                    const canScrollUp = node.scrollTop > 0;
                    if ((deltaY > 0 && canScrollDown) || (deltaY < 0 && canScrollUp)) return node;
                }
            }
        }
        return null;
    }

    private _forwardWheelToInnerScrollable(e: WheelEvent): void {
        if (!this._isEventOnOverlay(e.target)) return;
        const container = this._findInnerScrollableAt(e.clientX, e.clientY, e.deltaY);
        if (!container) return; // sin panel interno → scroll de página nativo
        e.preventDefault(); // evitar el doble desplazamiento (panel + página)
        container.scrollTop += e.deltaY;
        // El scroll del contenedor dispara el evento 'scroll' (capture) → _repositionOnScroll sigue al objetivo.
    }

    private _beginTouchForward(e: TouchEvent): void {
        if (e.touches.length !== 1 || !this._isEventOnOverlay(e.target)) {
            this._touchForward = null;
            return;
        }
        this._touchForward = { lastY: e.touches[0].clientY };
    }

    private _forwardTouchToInnerScrollable(e: TouchEvent): void {
        if (!this._touchForward || e.touches.length !== 1) return;
        const touch = e.touches[0];
        const delta = this._touchForward.lastY - touch.clientY; // dedo hacia arriba = contenido baja (delta > 0)
        this._touchForward.lastY = touch.clientY;
        if (delta === 0) return;
        if (this._touchForward.container === undefined) {
            this._touchForward.container = this._findInnerScrollableAt(touch.clientX, touch.clientY, delta);
        }
        if (!this._touchForward.container) return; // sin panel interno → paneo nativo de la página
        e.preventDefault();
        this._touchForward.container.scrollTop += delta;
    }

    // ─── Event Listeners ─────────────────────────────────────────────────────

    @HostListener('window:resize')
    onWindowResize(): void {
        if (!this.currentStep || !this.isVisible || this._resizeScheduled) return;
        this._resizeScheduled = true;
        this.isCalculating = true;
        this._cdRef.markForCheck();
        const seq = ++this._calcSeq;
        this._ngZone.runOutsideAngular(() => {
            requestAnimationFrame(() => {
                this._resizeScheduled = false;
                this._calculatePositions(this.currentStep!, seq)
                    .then(() => {
                        this._ngZone.run(() => {
                            if (this._destroyed || seq !== this._calcSeq) return;
                            this.isCalculating = false;
                            this._cdRef.detectChanges();
                            // Medir la altura real del popover renderizado y corregir el clamp/flip
                            // si difiere de la estimada (síncrono: antes del paint).
                            this._refinePopoverGeometry();
                        });
                    })
                    .catch(err => {
                        // Sin este catch, si el pipeline rechaza `isCalculating` queda en true → overlay
                        // congelado. Mismo recovery que en ngOnInit: limpiar carga y caer al popover centrado.
                        console.error('[OnboardingBlackout] Error recalculando posiciones en resize:', err);
                        this._ngZone.run(() => {
                            if (this._destroyed || seq !== this._calcSeq) return;
                            this.isCalculating = false;
                            this.elementNotFound = true;
                            this.popoverPosition = { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' };
                            this._cdRef.detectChanges();
                        });
                    });
            });
        });
    }

    // ─── Acciones del usuario ─────────────────────────────────────────────────

    onNext(): void {
        this.orchestrator.nextStep();
    }

    onSkip(): void {
        // onboarding_step_skipped: el task_id es el tourId del tour activo.
        const taskId = this.orchestrator.currentState.currentTour?.tourId;
        if (taskId) this.analytics.stepSkipped(taskId);
        this.orchestrator.endTour();
    }

    /**
     * Cierre por "Finalizar" (último paso): el usuario COMPLETÓ el tour. A diferencia de `onSkip()`, NO emite
     * `onboarding_step_skipped` (evita inflar las métricas de abandono al finalizar con éxito).
     */
    onFinish(): void {
        // onboarding_tour_completed: distingue un tour terminado con éxito de uno saltado (AC FINA-2633). Se
        // lee el taskId ANTES de endTour() (que limpia currentTour). NO emite `onboarding_step_skipped`.
        const taskId = this.orchestrator.currentState.currentTour?.tourId;
        if (taskId) this.analytics.tourCompleted(taskId);
        this.orchestrator.endTour();
    }

    get isLastStep(): boolean {
        return this.currentStepDisplay === this.totalSteps;
    }

    // ─── Pipeline de posicionamiento ──────────────────────────────────────────

    /**
     * Pipeline async de 5 fases para localizar y medir el targetElement.
     * Ver comentario del archivo para la explicacion completa.
     *
     * FASE 0 (nueva): ejecuta step.beforeShowAction si existe, y hace await.
     *                 Permite preparar el layout (cerrar sidebar, abrir carrito, etc.)
     *                 ANTES de buscar o medir el elemento. Principio "Cero Cables":
     *                 la logica de layout vive en el catalogo de tours, no en este componente.
     * FASE 4 incluye logica responsive:
     *  - Deteccion de movil via window.innerWidth <= MOBILE_BREAKPOINT.
     *  - Sobrescritura de placement lateral (left/right) a bottom/top.
     *  - Flip/clamp al viewport + calculo de --caret-x via _computePopoverPosition (unificado desktop/movil).
     */
    private async _calculatePositions(step: TourStep, seq: number): Promise<void> {
        // FASE 0: Lifecycle hook — prepara el layout antes de buscar el elemento.
        // Permite que el catalogo cierre el sidebar o abra paneles offcanvas en movil
        // sin acoplar este componente a los servicios de negocio.
        if (step.beforeShowAction) {
            await step.beforeShowAction();
        }
        // El paso pudo cambiar (o el tour cerrarse) durante el hook async → abortar sin escribir estado.
        if (seq !== this._calcSeq) return;

        // Guard defensivo: un selector vacío rompería document.querySelector(''). Se trata como
        // "elemento no encontrado" → fallback centrado. No debería llegar aquí (buildTourFromTask ya filtra
        // pasos sin selector), pero blinda ante cualquier otro origen que inyecte un paso vacío.
        const selector = step.targetElement?.trim();
        if (!selector) {
            console.warn('[OnboardingBlackout] Paso con target_selector vacío; se omite el spotlight (fallback centrado).');
            this.elementNotFound = true;
            this.spotlightRect = null;
            this.caretOffsetPx = null;
            this.popoverPosition = { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' };
            return;
        }

        // FASE 1: query sincrono (90% de los pasos) — primera coincidencia VISIBLE (FINA-3296)
        let targetEl: Element | null = this._queryVisibleElement(selector);

        // FASE 2: MutationObserver para elementos asincronos (cards post-HTTP)
        if (!targetEl) {
            console.info('[OnboardingBlackout] "' + selector + '" no encontrado sincrono. ' + 'Activando MutationObserver (' + WAIT_FOR_ELEMENT_TIMEOUT_MS + 'ms)...');
            targetEl = await this._waitForElement(selector);
            // Pudo cambiar el paso mientras esperábamos el MutationObserver → abortar.
            if (seq !== this._calcSeq) return;
        }

        // Fallback: elemento no encontrado tras el reintento (feature flag apagado / no renderiza).
        // Auto-omitir el paso de forma segura en vez de congelar con un popover centrado o mostrar errores de
        // DOM: avanzamos al siguiente (o cerramos si es el último) reutilizando el flujo normal del orquestador.
        // La nueva emisión de `tourState$` incrementa `_calcSeq` y dispara `blackoutShown` del paso siguiente,
        // así que la analítica y el token de secuencia quedan intactos; el `.then()` de este pipeline se
        // descarta por el guard `seq !== this._calcSeq` (no deja `isCalculating` colgado).
        if (!targetEl) {
            console.warn('[OnboardingBlackout] "' + step.targetElement + '" no apareció; se omite el paso automáticamente.');
            if (seq !== this._calcSeq) return;
            this._ngZone.run(() => {
                if (seq !== this._calcSeq) return;
                // FINA-3295 (A): la omisión ya no es silenciosa — evento de analítica para medir cuántos
                // pasos se pierden (y con qué selector) cuando el objetivo no llega a pintarse a tiempo.
                const tour = this.orchestrator.currentState.currentTour;
                if (tour) this.analytics.stepAutoSkipped(tour.tourId, step.targetElement, this.orchestrator.currentState.currentStepIndex);
                if (this.isLastStep) this.orchestrator.endTour();
                else this.orchestrator.nextStep();
            });
            return;
        }

        // FASE 2.5 (FINA-3297): traer el objetivo A LA VISTA si quedó fuera del viewport. Con el scroll del
        // documento habilitado durante el tour (FINA-3288), la página CONSERVA el desplazamiento del paso
        // anterior: en "Precios de venta" el usuario baja para leer el paso 2 y el objetivo del paso 3 ("Nuevo
        // canal", arriba de la página) quedaba medio fuera por arriba. También cubre objetivos que nacen fuera
        // (el botón de devolución en móvil, y=~1139). Solo se scrollea si hace falta (no mover la página entre
        // pasos cuyos objetivos ya se ven) y SIN 'smooth': el settle de FASE 3 absorbe el salto antes de medir.
        // scrollIntoView desplaza además los contenedores internos scrolleables (páginas sin scroll de
        // documento, como el POS). Durante el scroll, `_repositionOnScroll` es no-op (guard `isCalculating`).
        const preRect = targetEl.getBoundingClientRect();
        if (preRect.top < 0 || preRect.bottom > window.innerHeight) {
            targetEl.scrollIntoView({ block: 'center', inline: 'nearest' });
        }

        // FASE 3: delay fijo para absorber reflows de layout globales
        // (sidebar abriendose, animaciones de contenedor, scrollIntoView de FASE 2.5, etc.)
        await new Promise<void>(resolve => setTimeout(resolve, LAYOUT_SETTLE_MS));
        // Tras el settle, si el usuario avanzó de paso (o cerró el tour), no escribir la geometría vieja.
        if (seq !== this._calcSeq) return;

        // FASE 4: medir con geometria estabilizada
        this._applyGeometry(step, targetEl);
        // isCalculating se desactiva en el .then() del caller con detectChanges()
    }

    /**
     * Mide el elemento objetivo (coordenadas de VIEWPORT) y escribe la geometría del spotlight y del popover.
     * Síncrono y reutilizable: lo usa el pipeline (`_calculatePositions`, tras el settle) y el reposicionamiento
     * EN VIVO al hacer scroll (`_repositionOnScroll`). Como el spotlight/popover son `position: fixed` y se miden
     * con `getBoundingClientRect` (relativo al viewport), al desplazar la página el elemento objetivo cambia su
     * posición y basta re-medir para que el resaltado y el popover SIGAN al elemento (en vez de bloquear el
     * scroll). Así el usuario desplaza toda la pantalla y ve el contexto completo, como en el tour original.
     */
    private _applyGeometry(step: TourStep, targetEl: Element, clampToViewport: boolean = true): void {
        this.elementNotFound = false;
        const rect = targetEl.getBoundingClientRect();
        const isMobile = window.innerWidth <= MOBILE_BREAKPOINT;

        // Sobrescritura de placement en movil: left/right causan overflow horizontal.
        // Se fuerza a bottom si hay espacio suficiente, o top en caso contrario.
        let effectivePlacement = step.placement;
        if (isMobile && (step.placement === 'left' || step.placement === 'right')) {
            const spaceBelow = window.innerHeight - rect.bottom;
            effectivePlacement = spaceBelow >= MIN_SPACE_FOR_POPOVER_PX ? 'bottom' : 'top';
        }
        // FINA-3329 (CP-02): durante el reposicionamiento por SCROLL (`clampToViewport=false`) NO se
        // re-deriva el placement: se conserva el decidido al colocar el paso. Re-derivarlo (flip) en pleno
        // scroll haría saltar el popover de lado, y el clamp lo dejaba clavado a 16px del borde separado de
        // su objetivo. En ese modo el popover sigue RÍGIDO al elemento, como el spotlight.
        if (!clampToViewport) effectivePlacement = this.currentPlacement;

        // Cálculo ÚNICO para escritorio y móvil (FINA-3294/CB-16: el flip/clamp vertical era solo de
        // escritorio y en ≤768px "Omitir/Siguiente" quedaban bajo el borde). El placement puede cambiar
        // (flip/degradación lateral), así que se asigna el DEFINITIVO antes de calcular el spotlight
        // (su radio depende del placement).
        const { position, placement, caretOffset } = this._computePopoverPosition(rect, effectivePlacement, isMobile, clampToViewport);
        this.currentPlacement = placement;
        this.popoverPosition = position;
        this.caretOffsetPx = caretOffset;

        const spotlightRadius = this.currentPlacement === 'right' ? 0 : 8;
        this.spotlightRect = {
            top: rect.top - SPOTLIGHT_PADDING,
            left: rect.left - SPOTLIGHT_PADDING,
            width: rect.width + SPOTLIGHT_PADDING * 2,
            height: rect.height + SPOTLIGHT_PADDING * 2,
            borderRadius: spotlightRadius,
        };
    }

    /**
     * Reposiciona spotlight y popover mientras el usuario hace scroll, para que sigan pegados al elemento
     * objetivo (el scroll de la página está permitido durante el tour). Se registra fuera de la zona de Angular
     * y se throttlea con requestAnimationFrame; la escritura de estado reentra con `_cdRef.detectChanges()`.
     */
    private _repositionOnScroll(): void {
        if (this._destroyed || !this.isVisible || !this.currentStep || this.isCalculating || this.elementNotFound) return;
        const selector = this.currentStep.targetElement?.trim();
        if (!selector) return;
        const targetEl = this._queryVisibleElement(selector);
        if (!targetEl) return;
        this._ngZone.run(() => {
            if (this._destroyed || !this.currentStep) return;
            this.spotlightAnimated = false; // seguir al elemento al instante mientras se hace scroll (sin lag)
            // FINA-3329 (CP-02): sin clamp al viewport durante el scroll — el popover sigue rígido a su
            // objetivo (puede salir de la ventana, como el spotlight) en vez de quedar clavado a 16px del
            // borde superior separado del elemento que explica.
            this._applyGeometry(this.currentStep, targetEl, false);
            this._cdRef.detectChanges();
        });
    }

    /**
     * Primera coincidencia VISIBLE del selector (FINA-3296). En Registros de pedidos el ancla del paso 2 de
     * Devoluciones ([data-tour="btn-devolucion"]) existe DOS veces: la copia de la tabla de escritorio —oculta
     * por CSS bajo 768px, rect 0×0— aparece primero en el DOM, y `document.querySelector` la tomaba: el
     * spotlight se dibujaba en (−6,−6) con 12×12px sin resaltar nada. Se recorren TODAS las coincidencias y se
     * usa la primera con tamaño real (> 0×0). Nota: un elemento fuera del viewport por scroll SÍ tiene tamaño,
     * así que sigue siendo elegible (el reposicionamiento por scroll lo sigue). Sin coincidencia visible →
     * null: mismo flujo que "no encontrado" (espera del MutationObserver y, si no aparece, omisión del paso
     * con la advertencia existente), en vez de dibujar un spotlight vacío en la esquina.
     */
    private _queryVisibleElement(selector: string): Element | null {
        const matches = document.querySelectorAll(selector);
        for (let i = 0; i < matches.length; i++) {
            const rect = matches[i].getBoundingClientRect();
            if (rect.width > 0 && rect.height > 0) return matches[i];
        }
        return null;
    }

    /**
     * Espera a que un selector CSS aparezca VISIBLE en el DOM usando MutationObserver (la visibilidad la
     * decide `_queryVisibleElement` — FINA-3296: una copia oculta del ancla no cuenta como "encontrado").
     *
     * Garantias anti-fuga de memoria:
     *  1. disconnect() al encontrar el elemento.
     *  2. disconnect() + clearTimeout() al agotar el timeout.
     *  3. cleanup() centraliza ambos caminos.
     */
    private _waitForElement(selector: string, timeoutMs: number = WAIT_FOR_ELEMENT_TIMEOUT_MS): Promise<Element | null> {
        return new Promise<Element | null>(resolve => {
            const immediateEl = this._queryVisibleElement(selector);
            if (immediateEl) {
                resolve(immediateEl);
                return;
            }

            let observer: MutationObserver | null = null;
            let timeoutHandle: ReturnType<typeof setTimeout> | null = null;

            const cleanup = (): void => {
                if (observer !== null) {
                    observer.disconnect();
                    observer = null;
                }
                if (timeoutHandle !== null) {
                    clearTimeout(timeoutHandle);
                    timeoutHandle = null;
                }
            };

            observer = new MutationObserver(() => {
                const el = this._queryVisibleElement(selector);
                if (el) {
                    cleanup();
                    resolve(el);
                }
            });

            observer.observe(document.body, { childList: true, subtree: true });

            timeoutHandle = setTimeout(() => {
                cleanup();
                resolve(null);
            }, timeoutMs);
        });
    }

    // ─── Calculo de posicion del popover ──────────────────────────────────────

    /**
     * Posición del popover acotada al viewport — cálculo ÚNICO para escritorio y móvil (FINA-3288 y
     * FINA-3294/CB-16: el flip/clamp vertical era solo de escritorio y en ≤768px "Omitir/Siguiente" quedaban
     * bajo el borde inferior). En móvil el popover ocupa el ancho útil completo (vw − 2·margen); en escritorio,
     * su ancho fijo de CSS.
     *
     * Reglas, en orden (las 1, 2 y 4 solo con `clampToViewport`):
     *  1. Laterales (left/right, solo escritorio — en móvil ya vienen forzados a bottom/top): si el popover no
     *     cabe a ese lado, DEGRADAN a 'bottom'. Si caben, conservan su anclaje original.
     *  2. Flip vertical: 'bottom' que no cabe debajo voltea a 'top' si arriba sí cabe (y viceversa).
     *  3. Clamp horizontal + caret (--caret-x) apuntando al objetivo. SIEMPRE: la página no se desplaza en
     *     horizontal, así que no hay salto al reposicionar por scroll.
     *  4. Clamp vertical duro (ningún lado cabe): se prioriza que el FOOTER (Skip/Siguiente) quede visible.
     *
     * `clampToViewport=false` (FINA-3329/CP-02, reposicionamiento por SCROLL): sin flip, sin degradación y sin
     * clamp vertical — el popover sigue RÍGIDO al objetivo aunque salga de la ventana, igual que el spotlight.
     * Antes el clamp lo dejaba clavado a 16px del borde superior, separado de su objetivo.
     *
     * Usa `_popoverHeightPx` (altura real cacheada por `_refinePopoverGeometry`; estimación solo en la primera
     * pasada, corregida de forma síncrona antes del paint).
     */
    private _computePopoverPosition(
        rect: DOMRect,
        placement: TourStepPlacement,
        isMobile: boolean,
        clampToViewport: boolean,
    ): { position: PopoverPosition; placement: TourStepPlacement; caretOffset: number | null } {
        const pad = SPOTLIGHT_PADDING;
        const offset = POPOVER_OFFSET;
        const margin = SIDE_MARGIN_PX;
        const vw = window.innerWidth;
        const vh = window.innerHeight;
        const popW = isMobile ? vw - margin * 2 : Math.min(POPOVER_MAX_WIDTH, vw - margin * 2);
        const popH = this._popoverHeightPx;

        // 1. Laterales: conservar el anclaje original SOLO si caben horizontalmente; si no, degradar a bottom.
        //    Durante el scroll no se degrada (placement estable).
        let effective: TourStepPlacement = placement;
        if (clampToViewport) {
            if (effective === 'right' && rect.right + pad + offset + popW > vw - margin) effective = 'bottom';
            else if (effective === 'left' && rect.left - pad - offset - popW < margin) effective = 'bottom';
        }

        if (effective === 'right' || effective === 'left') {
            return {
                position: {
                    top: rect.top + rect.height / 2 + 'px',
                    left: effective === 'right' ? rect.right + pad + offset + 'px' : rect.left - pad - offset + 'px',
                    transform: effective === 'right' ? 'translateY(-50%)' : 'translate(-100%, -50%)',
                },
                placement: effective,
                caretOffset: null, // caret lateral: posición fija afinada en SCSS
            };
        }

        // 2. Flip vertical con la altura real del popover (solo al colocar; nunca en pleno scroll).
        if (clampToViewport) {
            const fitsBelow = rect.bottom + pad + offset + popH <= vh - margin;
            const fitsAbove = rect.top - pad - offset - popH >= margin;
            if (effective === 'bottom' && !fitsBelow && fitsAbove) effective = 'top';
            else if (effective === 'top' && !fitsAbove && fitsBelow) effective = 'bottom';
        }

        // 3. Clamp horizontal + caret apuntando al objetivo (escritorio y móvil por igual).
        const elementCenterX = rect.left + rect.width / 2;
        const idealLeft = elementCenterX - popW / 2;
        const clampedLeft = Math.max(margin, Math.min(idealLeft, vw - popW - margin));
        const caretOffset = Math.max(20, Math.min(elementCenterX - clampedLeft, popW - 20));

        // 4. Top en coordenadas absolutas (sin translateY(-100%): con la altura medida el clamp es consistente).
        //    Con clamp: vertical duro priorizando el footer. Sin clamp (scroll): posición cruda que sigue al
        //    objetivo fuera de la ventana si hace falta (FINA-3329).
        let top = effective === 'bottom' ? rect.bottom + pad + offset : rect.top - pad - offset - popH;
        if (clampToViewport) {
            const maxTop = vh - popH - margin;
            top = Math.min(top, maxTop); // nunca dejar Skip/Siguiente bajo el borde inferior
            top = Math.max(top, Math.min(margin, maxTop)); // no salir por arriba, salvo que no quepa entero
        }

        return {
            position: { top: top + 'px', left: clampedLeft + 'px', transform: 'none' },
            placement: effective,
            caretOffset,
        };
    }

    /**
     * Segunda pasada de posicionamiento: mide la altura REAL del popover ya renderizado (varía con el
     * contenido de cada paso), la cachea en `_popoverHeightPx` y, si difiere de la usada en la primera
     * pasada, re-aplica la geometría. Se invoca SÍNCRONAMENTE justo después del detectChanges() que pinta el
     * paso, por lo que la corrección ocurre antes del paint del navegador: no hay parpadeo visible.
     * Aplica también en móvil (FINA-3294/CB-16: su flip/clamp vertical usa la misma altura medida).
     */
    private _refinePopoverGeometry(): void {
        if (!this.currentStep || this.elementNotFound) return;
        const popoverEl = document.querySelector('.tour-popover') as HTMLElement | null;
        if (!popoverEl) return;
        const realHeight = popoverEl.offsetHeight;
        if (!realHeight || Math.abs(realHeight - this._popoverHeightPx) < 2) return;
        this._popoverHeightPx = realHeight;

        const selector = this.currentStep.targetElement?.trim();
        const targetEl = selector ? this._queryVisibleElement(selector) : null;
        if (!targetEl) return;
        this._applyGeometry(this.currentStep, targetEl);
        this._cdRef.detectChanges();
    }
}
